import React from 'react';
import ReactMarkdown from 'react-markdown';

import remarkGfm from 'remark-gfm';

import { CopyButton } from './CopyButton';

interface MarkdownRendererProps {
    content: string;
    isQuery: boolean;
    messageId: string;
}

// Advanced markdown components with Mermaid support
function getMarkdownComponents(isQuery: boolean, messageId: string) {
    return {
        // Text elements
        p: ({ children, ...props }: any) => (
            <p className="mb-3 leading-relaxed last:mb-0" {...props}>
                {children}
            </p>
        ),
        h1: ({ children, ...props }: any) => (
            <h1 className="text-2xl font-semibold mt-4 mb-2 leading-tight" {...props}>
                {children}
            </h1>
        ),
        h2: ({ children, ...props }: any) => (
            <h2 className="text-xl font-semibold mt-4 mb-2 leading-tight" {...props}>
                {children}
            </h2>
        ),
        h3: ({ children, ...props }: any) => (
            <h3 className="text-lg font-semibold mt-3 mb-2 leading-snug" {...props}>
                {children}
            </h3>
        ),
        h4: ({ children, ...props }: any) => (
            <h4 className="text-base font-semibold mt-3 mb-2" {...props}>
                {children}
            </h4>
        ),
        h5: ({ children, ...props }: any) => (
            <h5 className="text-base font-semibold mt-3 mb-2" {...props}>
                {children}
            </h5>
        ),
        h6: ({ children, ...props }: any) => (
            <h6 className="text-base font-semibold mt-3 mb-2" {...props}>
                {children}
            </h6>
        ),

        // Styling elements
        strong: ({ children }: any) => <strong className="font-semibold">{children}</strong>,
        em: ({ children }: any) => <em className="italic">{children}</em>,

        // Lists
        ul: ({ children, ...props }: any) => (
            <ul className="list-disc pl-6 my-2" {...props}>
                {children}
            </ul>
        ),
        ol: ({ children, ...props }: any) => (
            <ol className="list-decimal pl-6 my-2" {...props}>
                {children}
            </ol>
        ),
        li: ({ children, ...props }: any) => (
            <li className="mb-1 relative" {...props}>
                {children}
            </li>
        ),

        // Tables
        table: ({ children }: any) => (
            <div className="overflow-x-auto my-4">
                <table className="w-full border-collapse text-sm">{children}</table>
            </div>
        ),
        thead: ({ children }: any) => <thead>{children}</thead>,
        tbody: ({ children }: any) => <tbody>{children}</tbody>,
        tr: ({ children }: any) => <tr className="even:bg-gray-50 dark:even:bg-gray-800">{children}</tr>,
        th: ({ children }: any) => (
            <th className="border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-700 p-2 text-left font-semibold">
                {children}
            </th>
        ),
        td: ({ children }: any) => <td className="border border-gray-200 dark:border-gray-700 p-2">{children}</td>,

        // Links
        a: ({ href, children }: any) => (
            <a
                className="text-blue-600 dark:text-blue-400 underline font-medium hover:text-blue-800 dark:hover:text-blue-300"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
            >
                {children}
            </a>
        ),

        // Advanced code handling with Mermaid support
        code: ({ node, className, children, ...props }: any) => {
            const match = /language-(\w+)/.exec(className || '');
            const language = match ? match[1].split(':')[0] : '';
            const content = children?.toString() || '';

            // Generate a stable id for this code block
            const codeId = `${messageId}-${content.substring(0, 20).replace(/\W/g, '')}`;

            // Handle Mermaid diagrams
            if (language === 'mermaid') {
                return (
                    <div className="relative min-h-[100px] my-4 w-full border rounded-md p-4 overflow-hidden bg-white dark:bg-slate-900 dark:border-slate-700 shadow-sm">
                        <div className="mb-3 flex items-center justify-between">
                            <div className="flex items-center">
                                <div className="h-5 w-16 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 dark:from-slate-700 dark:via-slate-600 dark:to-slate-700 rounded animate-pulse"></div>
                                <div className="ml-2 h-5 w-24 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 dark:from-slate-700 dark:via-slate-600 dark:to-slate-700 rounded animate-pulse delay-100"></div>
                            </div>
                            <div className="text-xs px-2 py-1 rounded-full border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 animate-pulse">
                                <span className="font-mono">mermaid</span>
                            </div>
                        </div>
                        <div className="h-56 w-full bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 rounded-md flex items-center justify-center relative overflow-hidden backdrop-blur-sm shadow-sm">
                            <div className="absolute inset-0 bg-[linear-gradient(rgba(156,163,175,0.1)_1px,transparent_1px),linear-gradient(to_right,rgba(156,163,175,0.1)_1px,transparent_1px)] bg-[size:20px_20px]"></div>
                            <div className="text-sm text-slate-500 dark:text-slate-400 font-mono">
                                Mermaid diagram rendering...
                            </div>
                        </div>
                    </div>
                );
            }

            // Check if this is a code block or inline code
            const isCodeBlock =
                node?.position?.start?.line !== node?.position?.end?.line ||
                (node?.parent?.type === 'element' && node?.parent?.tagName === 'pre');

            // For code blocks vs inline code
            return isCodeBlock ? (
                <div className="my-3" key={codeId}>
                    <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 overflow-x-auto">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-mono text-gray-500 dark:text-gray-400">
                                {language || 'text'}
                            </span>
                            <CopyButton content={content} copyMessage="Code copied to clipboard" />
                        </div>
                        <pre className="text-sm font-mono">
                            <code className={`language-${language}`} {...props}>
                                {children}
                            </code>
                        </pre>
                    </div>
                </div>
            ) : (
                <code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-[0.9em] font-mono" {...props}>
                    {children}
                </code>
            );
        },

        // Pre handling
        pre: ({ children }: any) => <div className="my-0 p-0">{children}</div>,

        // Misc elements
        blockquote: ({ children }: any) => (
            <blockquote className="border-l-3 border-gray-200 dark:border-gray-700 pl-4 my-3 italic">
                {children}
            </blockquote>
        ),
        hr: () => <hr className="border-t border-gray-200 dark:border-gray-700 my-4" />,
        br: () => <br className="h-3" />,
    };
}

export const MarkdownRenderer = React.memo(({ content, isQuery, messageId }: MarkdownRendererProps) => {
    return (
        <div className="prose prose-sm dark:prose-invert max-w-none break-words overflow-hidden leading-relaxed">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={getMarkdownComponents(isQuery, messageId)}>
                {content}
            </ReactMarkdown>
        </div>
    );
});

MarkdownRenderer.displayName = 'MarkdownRenderer';
